import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Star, Quote, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpotlightsPage = () => {
    const [spotlights, setSpotlights] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpotlight, setEditingSpotlight] = useState(null);
    const [formData, setFormData] = useState({
        member_id: '',
        title: '',
        bio: '',
        quote: '',
        advice: '',
        image_url: '',
        is_featured: false
    });

    useEffect(() => {
        fetchSpotlights();
        fetchMembers();
    }, []);

    const fetchSpotlights = async () => {
        try {
            const response = await axios.get(`${API}/spotlights`);
            setSpotlights(response.data);
        } catch (error) {
            toast.error('Failed to load spotlights');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to load members');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingSpotlight) {
                await axios.put(`${API}/spotlights/${editingSpotlight.id}`, formData);
                toast.success('Spotlight updated successfully');
            } else {
                await axios.post(`${API}/spotlights`, formData);
                toast.success('Spotlight created successfully');
            }
            setIsModalOpen(false);
            resetForm();
            fetchSpotlights();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save spotlight');
        }
    };

    const handleEdit = (spotlight) => {
        setEditingSpotlight(spotlight);
        setFormData({
            member_id: spotlight.member_id,
            title: spotlight.title || '',
            bio: spotlight.bio,
            quote: spotlight.quote || '',
            advice: spotlight.advice || '',
            image_url: spotlight.image_url || '',
            is_featured: spotlight.is_featured
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (spotlightId) => {
        if (!window.confirm('Are you sure you want to delete this spotlight?')) return;
        try {
            await axios.delete(`${API}/spotlights/${spotlightId}`);
            toast.success('Spotlight deleted successfully');
            fetchSpotlights();
        } catch (error) {
            toast.error('Failed to delete spotlight');
        }
    };

    const handleToggleFeatured = async (spotlight) => {
        try {
            await axios.put(`${API}/spotlights/${spotlight.id}`, {
                is_featured: !spotlight.is_featured
            });
            toast.success(spotlight.is_featured ? 'Removed from featured' : 'Set as featured');
            fetchSpotlights();
        } catch (error) {
            toast.error('Failed to update spotlight');
        }
    };

    const resetForm = () => {
        setEditingSpotlight(null);
        setFormData({
            member_id: '',
            title: '',
            bio: '',
            quote: '',
            advice: '',
            image_url: '',
            is_featured: false
        });
    };

    const getMemberName = (memberId) => {
        const member = members.find(m => m.id === memberId);
        return member?.name || 'Unknown';
    };

    const getMemberDetails = (memberId) => {
        return members.find(m => m.id === memberId);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading spotlights...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="spotlights-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                        Brother Spotlights
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Highlight and celebrate community members
                    </p>
                </div>
                <Dialog open={isModalOpen} onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            data-testid="add-spotlight-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Spotlight
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                {editingSpotlight ? 'Edit Spotlight' : 'Create New Spotlight'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="member">Select Member *</Label>
                                <Select
                                    value={formData.member_id}
                                    onValueChange={(value) => setFormData({ ...formData, member_id: value })}
                                >
                                    <SelectTrigger data-testid="spotlight-member-select">
                                        <SelectValue placeholder="Choose a member to spotlight" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {members.map((member) => (
                                            <SelectItem key={member.id} value={member.id}>
                                                {member.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="title">Title/Role</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g., Research Engineer | Stanford University"
                                    data-testid="spotlight-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="bio">Bio *</Label>
                                <Textarea
                                    id="bio"
                                    value={formData.bio}
                                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                    required
                                    placeholder="Write about this brother's journey, background, and impact..."
                                    rows={4}
                                    data-testid="spotlight-bio-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="quote">Featured Quote</Label>
                                <Textarea
                                    id="quote"
                                    value={formData.quote}
                                    onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                                    placeholder="A memorable quote from this brother..."
                                    rows={2}
                                    data-testid="spotlight-quote-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="advice">Advice for the Community</Label>
                                <Textarea
                                    id="advice"
                                    value={formData.advice}
                                    onChange={(e) => setFormData({ ...formData, advice: e.target.value })}
                                    placeholder="Any wisdom or advice this brother would like to share..."
                                    rows={2}
                                    data-testid="spotlight-advice-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="image_url">Profile Image URL</Label>
                                <Input
                                    id="image_url"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    placeholder="https://..."
                                    data-testid="spotlight-image-input"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="is_featured"
                                    checked={formData.is_featured}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                                    data-testid="spotlight-featured-switch"
                                />
                                <Label htmlFor="is_featured">Set as Featured Spotlight</Label>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit"
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="save-spotlight-btn"
                                >
                                    {editingSpotlight ? 'Update Spotlight' : 'Create Spotlight'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Featured Spotlight */}
            {spotlights.find(s => s.is_featured) && (
                <Card className="border-[#5A6350] shadow-lg overflow-hidden" data-testid="featured-spotlight">
                    <div className="grid grid-cols-1 lg:grid-cols-12">
                        {/* Image */}
                        <div 
                            className="lg:col-span-4 h-64 lg:h-auto bg-cover bg-center relative"
                            style={{
                                backgroundImage: `url(${spotlights.find(s => s.is_featured)?.image_url || 'https://images.unsplash.com/photo-1625181796571-7f0d4571ab12?crop=entropy&cs=srgb&fm=jpg&q=85'})`
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/30 lg:bg-gradient-to-l" />
                            <Badge className="absolute top-4 left-4 bg-[#C48A54] text-white">
                                <Star className="w-3 h-3 mr-1" />
                                Featured
                            </Badge>
                        </div>
                        
                        {/* Content */}
                        <div className="lg:col-span-8 p-8">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-white font-['Playfair_Display']">
                                        {getMemberName(spotlights.find(s => s.is_featured)?.member_id)}
                                    </h2>
                                    {spotlights.find(s => s.is_featured)?.title && (
                                        <p className="text-[#C9A227] font-medium mt-1">
                                            {spotlights.find(s => s.is_featured)?.title}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="text-[#A0A0A0] leading-relaxed mb-6">
                                {spotlights.find(s => s.is_featured)?.bio}
                            </p>
                            {spotlights.find(s => s.is_featured)?.quote && (
                                <blockquote className="border-l-4 border-[#845642] pl-4 italic text-white">
                                    <Quote className="w-5 h-5 text-[#C9A227] mb-2" />
                                    "{spotlights.find(s => s.is_featured)?.quote}"
                                </blockquote>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* All Spotlights Grid */}
            {spotlights.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                    {spotlights.filter(s => !s.is_featured).map((spotlight) => {
                        const member = getMemberDetails(spotlight.member_id);
                        return (
                            <Card 
                                key={spotlight.id} 
                                className="border-[#5A6350] shadow-sm card-hover overflow-hidden"
                                data-testid={`spotlight-card-${spotlight.id}`}
                            >
                                {/* Image */}
                                <div 
                                    className="h-48 bg-cover bg-center relative"
                                    style={{
                                        backgroundImage: spotlight.image_url 
                                            ? `url(${spotlight.image_url})` 
                                            : 'url(https://images.pexels.com/photos/7526101/pexels-photo-7526101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <h3 className="text-white font-semibold text-lg">
                                            {getMemberName(spotlight.member_id)}
                                        </h3>
                                        {spotlight.title && (
                                            <p className="text-white/80 text-sm">{spotlight.title}</p>
                                        )}
                                    </div>
                                </div>

                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between mb-3">
                                        <p className="text-[#A0A0A0] text-sm line-clamp-3">
                                            {spotlight.bio}
                                        </p>
                                    </div>

                                    {spotlight.quote && (
                                        <div className="bg-[#3D4435] rounded-lg p-3 mb-4">
                                            <p className="text-sm italic text-white line-clamp-2">
                                                "{spotlight.quote}"
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-2">
                                        <Button
                                            size="sm"
                                            variant={spotlight.is_featured ? 'default' : 'outline'}
                                            onClick={() => handleToggleFeatured(spotlight)}
                                            className={spotlight.is_featured 
                                                ? 'bg-[#C48A54] hover:bg-[#C48A54]/90' 
                                                : 'border-[#C48A54] text-[#C48A54]'
                                            }
                                            data-testid={`toggle-featured-${spotlight.id}`}
                                        >
                                            <Star className="w-4 h-4 mr-1" />
                                            {spotlight.is_featured ? 'Featured' : 'Feature'}
                                        </Button>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleEdit(spotlight)}
                                                className="p-2 hover:bg-[#3D4435] rounded-lg transition-colors"
                                                data-testid={`edit-spotlight-${spotlight.id}`}
                                            >
                                                <Edit2 className="w-4 h-4 text-[#A0A0A0]" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(spotlight.id)}
                                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                                data-testid={`delete-spotlight-${spotlight.id}`}
                                            >
                                                <Trash2 className="w-4 h-4 text-[#A64444]" />
                                            </button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Star className="w-16 h-16 text-[#E6E1D8] mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No spotlights yet</h3>
                        <p className="text-[#A0A0A0] text-center mb-4">
                            Highlight a brother's story and contributions
                        </p>
                        <Button 
                            onClick={() => setIsModalOpen(true)}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Spotlight
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default SpotlightsPage;
